import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Button, Grid,
  Card, CardContent, Chip, Container
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ChatIcon from '@mui/icons-material/Chat'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'

const features = [
  {
    icon: <AutoAwesomeIcon sx={{ color: '#8B7FFF' }} />,
    bg: '#1A1530',
    title: 'Kundali reading',
    desc: 'Full personalised birth chart based on Vedic astrology principles',
    path: '/reading',
  },
  {
    icon: <ChatIcon sx={{ color: '#D4537E' }} />,
    bg: '#1A1225',
    title: 'Ask Pandit AI',
    desc: 'Love, career, health or destiny — ask anything, get guidance',
    path: '/ask',
  },
  {
    icon: <FavoriteIcon sx={{ color: '#D4537E' }} />,
    bg: '#1A1225',
    title: 'Compatibility checker',
    desc: 'Find out if you and your partner are cosmically matched',
    path: '/compatibility',
  },
  {
    icon: <CalendarMonthIcon sx={{ color: '#639922' }} />,
    bg: '#121A14',
    title: 'Daily horoscope',
    desc: 'Personalised prediction every morning based on your chart',
    path: '/pricing',
  },
]

const stats = [
  { value: '2.4L+', label: 'readings done' },
  { value: '4.9', label: 'avg rating' },
  { value: '12', label: 'zodiac signs' },
  { value: '100%', label: 'AI powered' },
]

export default function Home() {
  const navigate = useNavigate()

  return (
    <Box>
      <Box sx={{
        textAlign: 'center',
        py: 8, px: 3,
        borderBottom: '1px solid #1E1A3A',
      }}>
        <Chip
          icon={<AutoAwesomeIcon sx={{ fontSize: '14px !important',
            color: '#A89EFF !important' }} />}
          label="AI powered Vedic astrology"
          sx={{ mb: 3, fontSize: 12 }} />

        <Box sx={{
          width: 90, height: 90, borderRadius: '50%',
          bgcolor: '#1A1530', border: '1px solid #3D3580',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', mx: 'auto', mb: 3,
        }}>
          <AutoAwesomeIcon sx={{ fontSize: 36, color: '#8B7FFF' }} />
        </Box>

        <Typography variant="h3" fontWeight={500} gutterBottom
          sx={{ fontSize: { xs: 26, md: 36 }, color: '#E8E6FF' }}>
          Discover your destiny<br />
          through the{' '}
          <Box component="span" sx={{ color: '#8B7FFF' }}>stars</Box>
        </Typography>

        <Typography sx={{
          color: '#8B85B8', maxWidth: 420,
          mx: 'auto', mb: 4, fontSize: 14, lineHeight: 1.7,
        }}>
          Ancient Vedic wisdom meets modern AI. Get your personalised
          Kundali reading, daily horoscope, and life guidance — free to start.
        </Typography>

        <Box sx={{
          display: 'flex', gap: 2,
          justifyContent: 'center',
          flexWrap: 'wrap', mb: 5,
        }}>
          <Button variant="contained" size="large"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => navigate('/reading')}
            sx={{ px: 4, fontSize: 14 }}>
            Get my free reading
          </Button>
          <Button variant="outlined" size="large"
            onClick={() => navigate('/ask')}
            sx={{ px: 4, fontSize: 14 }}>
            Ask Pandit AI
          </Button>
        </Box>

        <Grid container spacing={2}
          sx={{ maxWidth: 500, mx: 'auto' }}>
          {stats.map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" fontWeight={600}
                    sx={{ color: '#A89EFF' }}>
                    {s.value}
                  </Typography>
                  <Typography variant="caption"
                    sx={{ color: '#8B85B8' }}>
                    {s.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" fontWeight={500}
          sx={{ mb: 2, color: '#E8E6FF', textAlign: 'center' }}>
          Everything you need
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
  
  {/* First row — 2 cards */}
  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
    {features.slice(0, 2).map(f => (
      <Card
        key={f.title}
        onClick={() => navigate(f.path)}
        sx={{
          cursor: 'pointer',
          width: 350,
          height: 180,
          transition: '.2s',
          '&:hover': { borderColor: '#3D3580' },
        }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: f.bg, display: 'flex',
            alignItems: 'center',
            justifyContent: 'center', mb: 2,
          }}>
            {f.icon}
          </Box>
          <Typography fontWeight={500} gutterBottom
            sx={{ color: '#E8E6FF', fontSize: 13 }}>
            {f.title}
          </Typography>
          <Typography variant="body2"
            sx={{ color: '#8B85B8', lineHeight: 1.6, fontSize: 12 }}>
            {f.desc}
          </Typography>
        </CardContent>
      </Card>
    ))}
  </Box>

  {/* Second row — 2 cards */}
  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
    {features.slice(2, 4).map(f => (
      <Card
        key={f.title}
        onClick={() => navigate(f.path)}
        sx={{
          cursor: 'pointer',
          width: 350,
          height: 180,
          transition: '.2s',
          '&:hover': { borderColor: '#3D3580' },
        }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: f.bg, display: 'flex',
            alignItems: 'center',
            justifyContent: 'center', mb: 2,
          }}>
            {f.icon}
          </Box>
          <Typography fontWeight={500} gutterBottom
            sx={{ color: '#E8E6FF', fontSize: 13 }}>
            {f.title}
          </Typography>
          <Typography variant="body2"
            sx={{ color: '#8B85B8', lineHeight: 1.6, fontSize: 12 }}>
            {f.desc}
          </Typography>
        </CardContent>
      </Card>
    ))}
  </Box>

</Box>
      </Container>
    </Box>
  )
}